'use client';

import { useCallback, useMemo } from 'react';
import {
  BlockStack,
  Button,
  InlineStack,
  Select,
  Text,
  TextField,
  Tabs,
  Box,
  Tooltip,
  Icon,
  Divider,
} from '@shopify/polaris';
import { PlusIcon, InfoIcon } from '@shopify/polaris-icons';

import { Image, StandaloneMediaAsset, PlacementType } from '@/types/media';
import MediaAssetCard from '../media-asset/MediaAssetCard';
import { InputWrapper } from '@/features/video-generation-stepper/inputs/InputWrapper';
import { cn } from '@/lib/utils';
import NextImage from 'next/image';
import { TAB_IDS } from '@/features/ai-background-asset-creator/components/AIBackgroundAssetCreator';

type AspectRatio = '9:16' | '16:9' | '1:1';

type ImageBackgroundGenerationProps = {
  prompt: string;
  isPromptLoading: boolean;
  generatedMediaAsset?: StandaloneMediaAsset | null;
  isGenerating: boolean;
  isError: boolean;
  hideUseAction?: boolean;
  setPrompt: (prompt: string) => void;
  aspectRatio?: AspectRatio;
  setAspectRatio?: (ratio: AspectRatio) => void;
  backgroundImage?: Image | null;
  setBackgroundImage?: (image: Image | null) => void;
  onAddBackgroundImage?: () => void;
  selectedTabId: string;
  setSelectedTabId: (id: string) => void;
  placementType?: PlacementType;
  setPlacementType?: (type: PlacementType) => void;
};

const ASPECT_RATIO_OPTIONS = [
  { label: '9:16 (Vertical)', value: '9:16' },
  { label: '16:9 (Horizontal)', value: '16:9' },
  { label: '1:1 (Square)', value: '1:1' },
];

const PLACEMENT_TYPE_OPTIONS = [
  { label: 'Static', value: PlacementType.ManualPadding },
  { label: 'Dynamic', value: PlacementType.ManualPlacement },
];

export const ImageBackgroundGeneration = ({
  prompt,
  isPromptLoading,
  generatedMediaAsset,
  isGenerating,
  isError,
  hideUseAction = false,
  setPrompt,
  aspectRatio,
  setAspectRatio,
  backgroundImage,
  setBackgroundImage,
  onAddBackgroundImage,
  selectedTabId,
  setSelectedTabId,
  placementType,
  setPlacementType,
}: ImageBackgroundGenerationProps) => {
  const tabs = useMemo(
    () => [
      {
        id: TAB_IDS.GENERATE_AI,
        content: 'Generate with Prompt',
        accessibilityLabel: 'Generate background using only an AI text prompt',
        panelID: `${TAB_IDS.GENERATE_AI}-panel-content`,
      },
      {
        id: TAB_IDS.USE_IMAGE,
        content: 'Use Image Reference',
        accessibilityLabel: 'Use an image as a reference for AI generation',
        panelID: `${TAB_IDS.USE_IMAGE}-panel-content`,
        disabled: !setBackgroundImage || !onAddBackgroundImage,
      },
    ],
    [setBackgroundImage, onAddBackgroundImage]
  );

  const handleTabChange = useCallback((selectedTabIndex: number) => setSelectedTabId(tabs[selectedTabIndex].id), []);

  const handleRemoveImage = () => {
    if (setBackgroundImage) {
      setBackgroundImage(null);
      setSelectedTabId(TAB_IDS.GENERATE_AI);
    }
  };

  const selectedTabIndex = useMemo(() => {
    return tabs.findIndex((tab) => tab.id === selectedTabId);
  }, [selectedTabId, tabs]);

  const useImageContent = setBackgroundImage && onAddBackgroundImage && (
    <Box>
      <BlockStack gap="400">
        <InlineStack gap="100" blockAlign="center">
          <Text as="p" variant="bodyMd" tone="subdued">
            Select an image to guide the AI generation.
          </Text>
          <Tooltip content="The AI will use this image for style, composition, and color inspiration, but won't create an exact copy.">
            <Icon source={InfoIcon} tone="subdued" />
          </Tooltip>
        </InlineStack>

        {backgroundImage ? (
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Box borderWidth="050" borderStyle="solid" borderColor="border-focus" borderRadius="200" shadow="200">
              <div className={cn('relative aspect-[9/16] h-[180px]')}>
                <NextImage
                  src={backgroundImage.src}
                  alt={backgroundImage.alt || 'Selected background reference image'}
                  fill
                  className={cn('rounded-[var(--p-border-radius-100)] object-contain')}
                />
              </div>
            </Box>

            <Button
              onClick={handleRemoveImage}
              variant="tertiary"
              tone="critical"
              disabled={isGenerating}
              accessibilityLabel="Remove reference image"
            >
              Remove
            </Button>
          </InlineStack>
        ) : (
          <Box>
            <Button icon={PlusIcon} onClick={onAddBackgroundImage} disabled={isGenerating}>
              Select Reference Image
            </Button>
          </Box>
        )}
      </BlockStack>
    </Box>
  );

  const generateAiContent = (
    <BlockStack gap="400">
      <Text as="p" variant="bodyMd" tone="subdued">
        {backgroundImage
          ? 'Optionally add a prompt to refine the generation based on the image reference.'
          : 'Describe the background you want the AI to create.'}
      </Text>
      <TextField
        label="AI Background Prompt"
        labelHidden
        value={prompt}
        loading={isPromptLoading}
        onChange={(value) => setPrompt(value)}
        autoComplete="off"
        multiline={3}
        placeholder={
          backgroundImage
            ? 'e.g., make it look like a painting, add stars'
            : 'e.g., A serene forest path bathed in morning sunlight, detailed'
        }
        disabled={isPromptLoading || isGenerating}
      />
    </BlockStack>
  );

  return (
    <BlockStack gap="400">
      <InputWrapper title="Background Source" description="Choose how to generate your background">
        <Tabs tabs={tabs} selected={selectedTabIndex} onSelect={handleTabChange} />
        {selectedTabId === TAB_IDS.GENERATE_AI && generateAiContent}
        {selectedTabId === TAB_IDS.USE_IMAGE && useImageContent}
      </InputWrapper>

      {(setAspectRatio || setPlacementType) && (
        <InputWrapper title="Settings" description="Adjust the settings for the AI generation">
          <InlineStack gap="400">
            {setAspectRatio && (
              <Select
                label="Aspect Ratio"
                options={ASPECT_RATIO_OPTIONS}
                value={aspectRatio || '16:9'}
                onChange={(value) => setAspectRatio(value as AspectRatio)}
                disabled={isGenerating}
              />
            )}

            {setPlacementType && (
              <Select
                label="Subject Placement and Size"
                options={PLACEMENT_TYPE_OPTIONS}
                value={placementType || PLACEMENT_TYPE_OPTIONS[0].value}
                onChange={(value) => setPlacementType(value as PlacementType)}
                disabled={isGenerating}
              />
            )}
          </InlineStack>
        </InputWrapper>
      )}

      {!isError && generatedMediaAsset && (
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            Generated Background
          </Text>
          <InlineStack align="center">
            <MediaAssetCard media={generatedMediaAsset} hideUseAction={hideUseAction} />
          </InlineStack>
        </BlockStack>
      )}
    </BlockStack>
  );
};
